import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Settings as SettingsIcon, Lock, Save, Shield } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { BiometricSettingsCard } from "@/components/BiometricSettingsCard";
import { PasswordInput } from "@/components/ui/password-input";
import { AdminLockSettingsCard } from "@/components/AdminLockSettingsCard";

export default function Settings() {
  const { user } = useAuth();
  const [form, setForm] = useState({ old_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [editPwd, setEditPwd] = useState({ current: "", next: "", confirm: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editMeta, setEditMeta] = useState({ has_custom_password: false, updated_at: null, updated_by: null });

  useEffect(() => {
    api.get("/config/edit-password").then((r) => setEditMeta(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.old_password || !form.new_password) {
      toast.error("Preencha os campos");
      return;
    }
    if (form.new_password.length < 4) {
      toast.error("Nova senha deve ter ao menos 4 caracteres");
      return;
    }
    if (form.new_password !== form.confirm) {
      toast.error("Confirmação de senha não confere");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        old_password: form.old_password,
        new_password: form.new_password,
      });
      toast.success("Senha alterada com sucesso");
      setForm({ old_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao alterar senha");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="bg-white rounded-2xl border border-slate-100 card-shadow p-6">
        <h2 className="font-heading text-lg font-bold text-[#1e3a8a] mb-4 flex items-center gap-2">
          <SettingsIcon className="w-5 h-5" />
          Perfil
        </h2>
        <div className="flex items-center gap-4">
          <Avatar className="w-16 h-16">
            <AvatarFallback className="bg-[#1e3a8a] text-white text-lg">
              {user?.name?.split(" ").map((p) => p[0]).slice(0, 2).join("") || "A"}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-semibold text-slate-900">{user?.name}</p>
            <p className="text-sm text-slate-600">{user?.email}</p>
            <span className="inline-block mt-1 text-[10px] bg-[#7c3aed]/10 text-[#7c3aed] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
              Administrador
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 card-shadow p-6">
        <h2 className="font-heading text-lg font-bold text-[#1e3a8a] mb-4 flex items-center gap-2">
          <Lock className="w-5 h-5" />
          Alterar senha
        </h2>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">Senha atual</label>
            <PasswordInput
              type="password"
              value={form.old_password}
              onChange={(e) => setForm({ ...form, old_password: e.target.value })}
              required
              data-testid="change-pwd-old"
              className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed]"
              autoComplete="current-password"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">Nova senha</label>
            <PasswordInput
              type="password"
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              required
              minLength={4}
              data-testid="change-pwd-new"
              className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed]"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">Confirmar nova senha</label>
            <PasswordInput
              type="password"
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              required
              data-testid="change-pwd-confirm"
              className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed]"
              autoComplete="new-password"
            />
          </div>
          <div className="flex justify-end pt-3 border-t border-slate-100">
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white"
              data-testid="change-pwd-submit"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Salvando..." : "Alterar senha"}
            </Button>
          </div>
        </form>
      </div>

      <BiometricSettingsCard />
      <AdminLockSettingsCard />

      <div className="bg-white rounded-2xl border border-slate-100 card-shadow p-6">
        <h2 className="font-heading text-lg font-bold text-[#1e3a8a] mb-2 flex items-center gap-2">
          <Shield className="w-5 h-5 text-amber-600" />
          Senha de edição/configurações do sistema
        </h2>
        <p className="text-xs text-slate-600 mb-4">
          Esta senha é exigida para <b>retroceder o status de um orçamento</b> e para <b>editar itens em orçamentos finalizados</b>. Senha padrão inicial: a mesma informada na implantação (fica escondida por segurança).
          {editMeta.has_custom_password && (
            <span className="block mt-1 text-green-700">
              Senha personalizada em uso{editMeta.updated_by ? ` (última alteração por ${editMeta.updated_by})` : ""}.
            </span>
          )}
        </p>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!editPwd.current || !editPwd.next) { toast.error("Preencha os campos"); return; }
            if (editPwd.next.length < 4) { toast.error("Nova senha precisa ter ao menos 4 caracteres"); return; }
            if (editPwd.next !== editPwd.confirm) { toast.error("Confirmação não confere"); return; }
            setSavingEdit(true);
            try {
              await api.put("/config/edit-password", { current_password: editPwd.current, new_password: editPwd.next });
              toast.success("Senha de edição atualizada");
              setEditPwd({ current: "", next: "", confirm: "" });
              const r = await api.get("/config/edit-password");
              setEditMeta(r.data);
            } catch (err) {
              toast.error(err.response?.data?.detail || "Erro");
            } finally { setSavingEdit(false); }
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">Senha atual</label>
            <PasswordInput
              type="password"
              value={editPwd.current}
              onChange={(e) => setEditPwd({ ...editPwd, current: e.target.value })}
              required
              data-testid="edit-pwd-current"
              className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed]"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Nova senha</label>
              <PasswordInput
                type="password"
                value={editPwd.next}
                onChange={(e) => setEditPwd({ ...editPwd, next: e.target.value })}
                required
                minLength={4}
                data-testid="edit-pwd-new"
                className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Confirmar</label>
              <PasswordInput
                type="password"
                value={editPwd.confirm}
                onChange={(e) => setEditPwd({ ...editPwd, confirm: e.target.value })}
                required
                data-testid="edit-pwd-confirm"
                className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed]"
              />
            </div>
          </div>
          <div className="flex justify-end pt-3 border-t border-slate-100">
            <Button
              type="submit"
              disabled={savingEdit}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="edit-pwd-submit"
            >
              <Save className="w-4 h-4 mr-2" />
              {savingEdit ? "Salvando..." : "Atualizar senha de edição"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
